/* eslint-disable react/prop-types */
/**
 * Cash Ladder — will the money landing in the bank cover the flights we have
 * to buy?
 *
 * The model is server/cash-ladder.js (scheduled installments in, airfare out),
 * restated on a DEPOSIT basis by server/cash-ladder-deposits.js. The
 * distinction is the reason this page exists: a customer charge is not a bank
 * deposit, and on a live 6-week window the gap was 10.1% before committed
 * Defera Credit was even counted. The reconciliation strip below is that gap,
 * itemised, so the headline figure can never quietly drift back to the charge
 * basis.
 *
 * Renders a STORED snapshot (migration 152) and regenerates only when an admin
 * presses Refresh — a live run costs ~47 Stripe subscription retrievals. Same
 * posture as Risk Review, and for the same reason.
 *
 * Layout: single responsive tree, no duplicated content.
 *   - md+ : the weekly ladder renders as a table.
 *   - <md : the same weeks render as stacked cards, because a six-column
 *           money table on a phone either clips or scrolls sideways.
 * Nothing overflows horizontally at any width.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Scale,
  RefreshCw,
  AlertTriangle,
  TrendingDown,
  Plane,
  Wallet,
  Info,
  Clock,
  Lock,
} from 'lucide-react';
import { User } from '@/api/entities';
import { getApiUrl } from '@/lib/apiUrls';
import CashLadderSimulator from '@/components/admin/CashLadderSimulator';

/** Bump when the payload shape changes — a stored snapshot written by an older
 *  build is discarded rather than rendered through the wrong component. v4: one
 *  balance (airfare due the day before departure) and in-transit deposits. v5:
 *  airfare due at payoff or the day before departure, whichever is first, plus
 *  the paid-off queue and the payoff run rate. v6: airfare still to buy is
 *  priced at the latest price check for the same flights when one is on file. */
const CACHE_VERSION = 'v6';

/** Shared surfaces, matching RiskReview / SettlementDesk. */
const PANEL =
  'rounded-2xl border border-white/[0.08] bg-zinc-950/60 backdrop-blur-2xl ' +
  'shadow-[0_24px_80px_-40px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.055)]';
const INSET = 'rounded-xl border border-white/[0.06] bg-white/[0.025]';

const usd = (cents) => {
  const value = (cents || 0) / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return value < 0 ? `−${formatted}` : formatted;
};
const usdExact = (cents) => {
  const value = (cents || 0) / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
  }).format(Math.abs(value));
  return value < 0 ? `−${formatted}` : formatted;
};

const parseDay = (day) => {
  if (!day) return null;
  const [y, m, d] = String(day).split('-').map(Number);
  return new Date(y, m - 1, d);
};
const formatDay = (day) => {
  const d = parseDay(day);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
};
/** "Sep 20 – 26" when a range sits inside one month, "Sep 27 – Oct 3" when it
 *  straddles two. Repeating the month inside a single-month week is noise in a
 *  column people scan for money, not dates. */
export const formatRange = (from, to) => {
  const a = parseDay(from);
  const b = parseDay(to);
  if (!a || !b) return '—';
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${formatDay(from)} – ${b.getDate()}`;
  }
  return `${formatDay(from)} – ${formatDay(to)}`;
};

/** A step's dates, however the payload spells them. `label` is the pre-v2
 *  server-rendered ISO string; it is read only so an older snapshot still
 *  renders something while the page waits for a Refresh. */
export const stepDates = (s) => {
  if (s.kind === 'now') return 'Today';
  if (s.start && s.end) return formatRange(s.start, s.end);
  return s.label || '';
};
const agoLabel = (iso) => {
  if (!iso) return 'never';
  const mins = Math.floor((Date.now() - Date.parse(iso)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* ── Primitives ─────────────────────────────────────────────────────────── */

const TONE = {
  good: { grad: 'from-emerald-400/25 via-white/[0.06] to-transparent', label: 'text-emerald-300/75', value: 'text-emerald-200' },
  bad: { grad: 'from-rose-400/25 via-white/[0.06] to-transparent', label: 'text-rose-300/75', value: 'text-rose-200' },
  brand: { grad: 'from-brand-300/25 via-white/[0.06] to-transparent', label: 'text-brand-200/75', value: 'text-white/90' },
  neutral: { grad: 'from-white/[0.10] via-white/[0.04] to-transparent', label: 'text-stone-400', value: 'text-white/90' },
};

function StatTile({ icon: Icon, label, value, sub, tone = 'neutral' }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div className={`rounded-2xl p-px bg-gradient-to-br ${t.grad}`}>
      <div className="rounded-2xl bg-zinc-950/85 border border-white/[0.055] px-3.5 py-3 h-full flex flex-col gap-1.5">
        <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-medium ${t.label}`}>
          <Icon className="w-3 h-3 shrink-0" strokeWidth={1.5} />
          <span className="min-w-0 truncate">{label}</span>
        </div>
        <p className={`text-[26px] sm:text-3xl font-extralight leading-none tabular-nums ${t.value} break-words`}>
          {value}
        </p>
        {sub && <p className="text-white/40 text-xs font-normal leading-snug">{sub}</p>}
      </div>
    </div>
  );
}

/** One line of the gross → deposit reconciliation. */
function ReconRow({ label, value, note, negative = false, positive = false, strong = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className={`${strong ? 'text-white/85' : 'text-white/60'} text-[13px] font-normal`}>{label}</p>
        {note && <p className="text-white/35 text-[11.5px] font-normal leading-snug">{note}</p>}
      </div>
      <p
        className={`shrink-0 tabular-nums text-[14px] font-normal ${
          strong ? 'text-white/90' : negative ? 'text-rose-300/90' : positive ? 'text-emerald-300/90' : 'text-white/70'
        }`}
      >
        {negative ? `−${usdExact(Math.abs(value))}` : positive ? `+${usdExact(value)}` : usdExact(value)}
      </p>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export default function CashLadder() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);

  const canAccess =
    !!currentUser &&
    (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  const authedFetch = useCallback(async (path, init = {}) => {
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const resp = await fetch(getApiUrl(path), {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${session.access_token}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
      credentials: 'omit',
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json.ok) throw new Error(json?.error || `Request failed (${resp.status})`);
    return json;
  }, []);

  const load = useCallback(async () => {
    if (!canAccess) return;
    try {
      setError(null);
      const json = await authedFetch('admin/cash-ladder');
      setSnapshot(json.snapshot || null);
    } catch (err) {
      setError(err?.message || 'Could not load the cash ladder.');
    } finally {
      setIsLoading(false);
    }
  }, [canAccess, authedFetch]);

  const refresh = useCallback(async () => {
    if (!canAccess) return;
    try {
      setIsRefreshing(true);
      setError(null);
      const json = await authedFetch('admin/cash-ladder/refresh', {
        method: 'POST',
        body: JSON.stringify({ weeks: 6, cache_version: CACHE_VERSION }),
      });
      setSnapshot(json.snapshot || null);
    } catch (err) {
      setError(err?.message || 'Cash ladder refresh failed.');
    } finally {
      setIsRefreshing(false);
    }
  }, [canAccess, authedFetch]);

  useEffect(() => {
    (async () => {
      try { setCurrentUser(await User.me()); } catch { setCurrentUser(null); } finally { setIsLoading(false); }
    })();
  }, []);

  useEffect(() => { if (canAccess) load(); }, [canAccess, load]);

  /** A snapshot written by an older build renders through the wrong component,
   *  so treat a version mismatch as "no snapshot" rather than a stale page. */
  const data = useMemo(() => {
    if (!snapshot?.payload) return null;
    if (snapshot.cache_version !== CACHE_VERSION) return null;
    return snapshot.payload;
  }, [snapshot]);

  const versionMismatch = Boolean(snapshot?.payload && snapshot.cache_version !== CACHE_VERSION);
  const walk = data?.balance || null;

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <p className="text-white/60 font-normal">Loading…</p>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <p className="text-white/85 font-normal">Administrator access required.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cash Ladder - Defera</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-5 sm:py-7 overflow-x-hidden">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-brand-500/[0.12] border border-brand-400/20 flex items-center justify-center shrink-0">
              <Scale className="w-4 h-4 text-brand-200" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extralight text-white leading-tight">Cash Ladder</h1>
              <p className="text-white/45 text-[13px] font-normal leading-snug">
                Money reaching the bank, against the airfare still to buy as trips pay off or depart.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-5">
          <button
            type="button"
            onClick={refresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[13px] font-medium text-white/70 hover:text-white transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Recomputing' : 'Refresh'}
          </button>
          {data && (
            <p className="text-white/35 text-[12px] font-normal">
              Generated {agoLabel(snapshot?.generated_at)}
            </p>
          )}
        </div>

        {error && (
          <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-rose-400/25 bg-rose-500/[0.06] px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
            <p className="text-rose-100/90 text-[13.5px] font-normal min-w-0 break-words">{error}</p>
          </div>
        )}

        {!data && (
          <div className={`${PANEL} px-4 py-8 text-center`}>
            <p className="text-white/85 text-[15px] font-normal mb-1.5">
              {versionMismatch ? 'This snapshot was written by an older build.' : 'No snapshot yet.'}
            </p>
            <p className="text-white/45 text-[13.5px] font-normal mb-4 max-w-md mx-auto leading-relaxed">
              A run walks every active trip and reads each Stripe subscription&rsquo;s locked
              schedule, so it happens on command rather than on every visit. Takes a few seconds.
            </p>
            <button
              type="button"
              onClick={refresh}
              disabled={isRefreshing}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-brand-500/20 border border-brand-400/30 text-[13px] font-medium text-brand-100 hover:bg-brand-500/30 transition disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Recomputing…' : 'Generate the ladder'}
            </button>
          </div>
        )}

        {data && walk && (
          <>
            {/* Opening cash is hand-entered. Say so, and say how old it is —
                every balance on this page is anchored to it. */}
            {data.opening.stale && (
              <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-amber-100/90 text-[13.5px] font-normal">
                    Opening cash is {data.opening.age_days} days old.
                  </p>
                  <p className="text-amber-100/55 text-[13px] font-normal leading-relaxed">
                    {usdExact(data.opening.cash_on_hand_cents)} was entered by hand on{' '}
                    {formatDay(String(data.opening.updated_at).slice(0, 10))} and is not derived from
                    the bank. Every balance and low point below is anchored to it.
                  </p>
                </div>
              </div>
            )}

            {/* Without the in-transit read the near weeks are missing money that is
                already charged. Say so rather than show a trough that isn't real. */}
            {data.inflow.in_transit_available === false && (
              <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-amber-100/90 text-[13.5px] font-normal">
                    Money already charged is missing from this run.
                  </p>
                  <p className="text-amber-100/55 text-[13px] font-normal leading-relaxed break-words">
                    The Settlement Desk could not be read
                    {data.inflow.in_transit_error ? ` (${data.inflow.in_transit_error})` : ''}, so batches
                    and payouts still on their way to the bank are not in the weeks below. Refresh to try again.
                  </p>
                </div>
              </div>
            )}

            {/* Headline */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mb-3">
              <StatTile
                icon={Wallet}
                tone="good"
                label={`Reaches the bank, ${data.horizon.weeks} weeks`}
                value={usd(data.inflow.deposit_cents)}
                sub={data.inflow.in_transit_cents > 0
                  ? `includes ${usd(data.inflow.in_transit_cents)} already charged, on its way`
                  : 'after fees, reserve and credit'}
              />
              <StatTile
                icon={Plane}
                tone="brand"
                label="Airfare to buy"
                value={usd(walk.total_outlay_cents)}
                sub={`${data.trips.to_ticket} trip${data.trips.to_ticket === 1 ? '' : 's'} to ticket · each due when it pays off or the day before it departs, whichever is first${
                  data.outlay.insurance_cents > 0 ? ` · includes ${usd(data.outlay.insurance_cents)} of Faye policies to buy` : ''}`}
              />
              <StatTile
                icon={TrendingDown}
                tone={walk.low_point_cents < 0 ? 'bad' : 'good'}
                label="Low point"
                value={usd(walk.low_point_cents)}
                sub={`at ${walk.low_point_at} · ends ${usd(walk.ending_cents)}`}
              />
            </div>

            {/* Pipeline — the whole book in flight, not the remainder. Sits above
                the deposit reconciliation because it is the wider frame: this
                is how much business is in the air; everything below is about
                the timing of the cash inside it. */}
            {data.pipeline && (
              <div className={`${PANEL} px-4 py-4 mb-3`}>
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-3">
                  <h2 className="text-white/85 text-[15px] font-medium">Pipeline — full trip value in flight</h2>
                  <p className="text-white/40 text-[11.5px] font-normal">
                    {data.pipeline.trips} trips not paid off
                    {data.pipeline.unresolved_stripe_trips > 0
                      && ` · ${data.pipeline.unresolved_stripe_trips} unresolved on Stripe`}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-3">
                  <div className={`${INSET} px-3.5 py-3`}>
                    <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">Total trip value</p>
                    <p className="text-[26px] font-extralight leading-none tabular-nums text-white/90 mt-1">
                      {usd(data.pipeline.total_cents)}
                    </p>
                  </div>
                  <div className={`${INSET} px-3.5 py-3`}>
                    <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">Collected</p>
                    <p className="text-[26px] font-extralight leading-none tabular-nums text-emerald-300 mt-1">
                      {usd(data.pipeline.paid_cents)}
                    </p>
                    {data.pipeline.collected_share != null && (
                      <p className="text-white/35 text-[11px] font-normal mt-1">
                        {(data.pipeline.collected_share * 100).toFixed(1)}% of the book
                      </p>
                    )}
                  </div>
                  <div className={`${INSET} px-3.5 py-3`}>
                    <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">Still to collect</p>
                    <p className="text-[26px] font-extralight leading-none tabular-nums text-amber-300 mt-1">
                      {usd(data.pipeline.outstanding_cents)}
                    </p>
                    {data.pipeline.receivable_outstanding_cents !== data.pipeline.outstanding_cents && (
                      <p className="text-white/35 text-[11px] font-normal mt-1 leading-snug">
                        {usdExact(data.pipeline.receivable_outstanding_cents)} of it owed to us
                      </p>
                    )}
                    {data.pipeline.components?.insurance_outstanding_cents > 0 && (
                      <p className="text-white/35 text-[11px] font-normal mt-1 leading-snug">
                        includes {usdExact(data.pipeline.components.insurance_outstanding_cents)} of Faye travel insurance
                      </p>
                    )}
                  </div>
                </div>

                {/* Per-product split. Stacks on a phone; never a table. */}
                <div className="space-y-1.5">
                  {(data.pipeline.buckets || []).map((b) => (
                    <div key={b.key} className={`${INSET} px-3.5 py-2.5 flex items-center justify-between gap-3`}>
                      <div className="min-w-0">
                        <p className="text-white/85 text-[13.5px] font-normal">{b.label}</p>
                        <p className="text-white/40 text-[11.5px] font-normal">
                          {b.trips} trip{b.trips === 1 ? '' : 's'} · {usdExact(b.paid_cents)} collected
                          {!b.is_receivable && ' · processor funded us up front'}
                        </p>
                      </div>
                      <p className="text-white/85 text-[14px] font-normal tabular-nums shrink-0">
                        {usdExact(b.total_cents)}
                      </p>
                    </div>
                  ))}
                </div>

                {data.pipeline.components && (
                  <p className="text-white/35 text-[11.5px] font-normal mt-2.5 leading-relaxed">
                    Made of airfare {usdExact(data.pipeline.components.fare_cents)}, service fees{' '}
                    {usdExact(data.pipeline.components.service_fee_cents)}, hold fees{' '}
                    {usdExact(data.pipeline.components.hold_fee_cents)}
                    {data.pipeline.components.other_cents !== 0 && (
                      <>, and {usdExact(data.pipeline.components.other_cents)} of hotel on packages plus
                        seats financed at checkout</>
                    )}.
                    {data.pipeline.components.insurance_cents > 0 && (
                      <> Faye travel insurance of {usdExact(data.pipeline.components.insurance_cents)} is
                        collected for Faye, so it is outside the trip value and collected figures.</>
                    )}
                    {data.defaulted?.trips > 0 && (
                      <> Excludes {data.defaulted.trips} defaulted trips worth{' '}
                        {usdExact(data.defaulted.total_cents)} — written off, not in flight.</>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* The reconciliation — the reason this page is not the charge basis */}
            <div className={`${PANEL} px-4 py-4 mb-3`}>
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-2">
                <h2 className="text-white/85 text-[15px] font-medium">What reaches the bank, and when</h2>
                {data.inflow.overstatement_share != null && (
                  <p className="text-white/40 text-[11.5px] font-normal">
                    the charge basis runs {(data.inflow.overstatement_share * 100).toFixed(1)}% high
                  </p>
                )}
              </div>
              <div className="divide-y divide-white/[0.05]">
                <ReconRow
                  label="Scheduled to be charged"
                  value={data.inflow.gross_scheduled_cents}
                  note="what customers are billed over the window"
                  strong
                />
                <ReconRow
                  label="Committed Defera Credit"
                  value={data.inflow.credit_netted_cents}
                  note="future cycles already covered — they net to $0 and collect no cash"
                  negative
                />
                <ReconRow
                  label="Processing"
                  value={data.inflow.processing_cents}
                  note="3.95% NMI · 4.4% Stripe — never arrives"
                  negative
                />
                <ReconRow
                  label="Reserve withheld"
                  value={data.inflow.reserve_withheld_cents}
                  note={`10% of every NMI batch — not lost, released ${formatDay(data.inflow.reserve_release_day)}${data.inflow.reserve_release_in_horizon ? ' (counted below)' : ', after this window'}`}
                  negative
                />
                {data.inflow.slips_past_horizon_cents > 0 && (
                  <ReconRow
                    label="Funds after the window"
                    value={data.inflow.slips_past_horizon_cents}
                    note="charged inside the window, but NMI funds 4–6 days later"
                    negative
                  />
                )}
                {data.inflow.in_transit_cents > 0 && (
                  <ReconRow
                    label="Already charged, on its way"
                    value={data.inflow.in_transit_cents}
                    note="NMI batches not yet funded, tonight's batch and Stripe payouts, each on the day it lands"
                    positive
                  />
                )}
                {data.inflow.reserve_release_in_horizon && data.inflow.reserve_release_cents > 0 && (
                  <ReconRow
                    label="Reserve released"
                    value={data.inflow.reserve_release_cents}
                    note={`every dollar NMI has withheld, back on ${formatDay(data.inflow.reserve_release_day)}`}
                    positive
                  />
                )}
                <ReconRow
                  label="Reaches the bank"
                  value={data.inflow.deposit_cents}
                  note={formatRange(data.horizon.from, data.horizon.to)}
                  strong
                />
              </div>
            </div>

            {/* Weekly ladder — table on md+, cards below */}
            <div className={`${PANEL} px-4 py-4 mb-3`}>
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-3">
                <h2 className="text-white/85 text-[15px] font-medium">Week by week</h2>
                <p className="text-white/40 text-[11.5px] font-normal">
                  deposits on the day they land · airfare at payoff or the day before departure ·
                  {' '}opening {usdExact(data.opening.cash_on_hand_cents)}
                </p>
              </div>

              {/* md+ table */}
              <div className="hidden md:block overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-white/40 text-[11px] uppercase tracking-[0.1em]">
                      <th className="text-left font-medium pb-2">Period</th>
                      <th className="text-right font-medium pb-2">Deposits</th>
                      <th className="text-right font-medium pb-2">Airfare</th>
                      <th className="text-right font-medium pb-2">Cover</th>
                      <th className="text-right font-medium pb-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walk.steps.map((s) => (
                      <tr key={s.period} className="border-t border-white/[0.05]">
                        <td className="py-2.5 pr-3">
                          <span className="text-white/85 font-normal">{s.period}</span>
                          <span className="text-white/35 ml-2">{stepDates(s)}</span>
                        </td>
                        <td className="py-2.5 text-right tabular-nums text-emerald-300/90">
                          {s.deposit_cents ? usdExact(s.deposit_cents) : '—'}
                        </td>
                        <td className="py-2.5 text-right tabular-nums text-rose-300/90">
                          {s.outlay_cents ? `−${usdExact(s.outlay_cents)}` : '—'}
                        </td>
                        <td className="py-2.5 text-right tabular-nums text-white/55">
                          {s.cover == null ? '—' : `${s.cover.toFixed(2)}×`}
                        </td>
                        <td className={`py-2.5 text-right tabular-nums ${s.balance_cents < 0 ? 'text-rose-300' : 'text-white/85'}`}>
                          {usdExact(s.balance_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* <md cards — same data, nothing hidden */}
              <div className="md:hidden space-y-2">
                {walk.steps.map((s) => (
                  <div key={s.period} className={`${INSET} px-3.5 py-3`}>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-white/85 text-[14px] font-normal">
                        {s.period}
                        <span className="text-white/35 ml-2 text-[12.5px]">{stepDates(s)}</span>
                      </p>
                      <p className={`tabular-nums text-[15px] font-normal shrink-0 ${s.balance_cents < 0 ? 'text-rose-300' : 'text-white/85'}`}>
                        {usdExact(s.balance_cents)}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-white/40 text-[10.5px] uppercase tracking-[0.1em] font-normal">In</p>
                        <p className="text-emerald-300/90 text-[13px] font-normal tabular-nums">
                          {s.deposit_cents ? usdExact(s.deposit_cents) : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/40 text-[10.5px] uppercase tracking-[0.1em] font-normal">Out</p>
                        <p className="text-rose-300/90 text-[13px] font-normal tabular-nums">
                          {s.outlay_cents ? `−${usdExact(s.outlay_cents)}` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/40 text-[10.5px] uppercase tracking-[0.1em] font-normal">Cover</p>
                        <p className="text-white/70 text-[13px] font-normal tabular-nums">
                          {s.cover == null ? '—' : `${s.cover.toFixed(2)}×`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* The work queue: what has to be bought now, and records that need fixing.
                Paid-off trips get their own list; they are left out of the two below
                so no trip shows twice. */}
            {(() => {
              const paidOff = data.outlay.paid_off || [];
              const dueNow = (data.outlay.due_now || []).filter((r) => r.trigger !== 'paid_off');
              const thisWeek = (data.outlay.this_week || []).filter((r) => r.trigger !== 'paid_off');
              const sum = (rows) => rows.reduce((t, r) => t + (r.cost_cents || 0), 0);
              if (!(paidOff.length || dueNow.length || thisWeek.length || data.outlay.departed_unbooked?.length
                || data.outlay.insurance_due_now?.length)) return null;
              return (
              <div className={`${PANEL} px-4 py-4 mb-3`}>
                <h2 className="text-white/85 text-[15px] font-medium mb-1">Tickets to buy</h2>
                <p className="text-white/45 text-[12px] font-normal mb-3" data-testid="latest-price-note">
                  {data.outlay.latest_price_trips > 0
                    ? `Airfare uses the latest price on file for ${data.outlay.latest_price_trips} trip${data.outlay.latest_price_trips === 1 ? '' : 's'}: ${data.outlay.latest_price_change_cents >= 0 ? '+' : '−'}${usdExact(Math.abs(data.outlay.latest_price_change_cents))} against their booked fares. The rest are at the booked fare.`
                    : 'Airfare is at the booked fare: no newer price is on file for these trips.'}
                </p>

                {paidOff.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-rose-200/90 text-[13px] font-medium">Paid off — owed a ticket</p>
                      <p className="text-rose-200/90 text-[13px] font-normal tabular-nums shrink-0">
                        {usdExact(data.outlay.paid_off_cents)}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {paidOff.map((r) => (
                        <div key={r.ref} className={`${INSET} px-3.5 py-2.5 flex items-center justify-between gap-3`}>
                          <div className="min-w-0">
                            <p className="text-white/85 text-[13.5px] font-normal">{r.ref}</p>
                            <p className="text-white/40 text-[11.5px] font-normal">
                              {r.paid_off_on ? `paid off ${formatDay(r.paid_off_on)} · ` : ''}release by {formatDay(r.release_by)}
                              {r.departs ? ` · departs ${formatDay(r.departs)}` : ''}
                              {r.insurance_cents > 0 && ` · includes ${usdExact(r.insurance_cents)} Faye policy`}
                              {r.fare_basis === 'latest_price' && ` · priced ${formatDay(r.priced_on)} (booked ${usdExact(r.booked_cents)})`}
                            </p>
                          </div>
                          <p className="text-white/85 text-[14px] font-normal tabular-nums shrink-0">
                            {usdExact(r.cost_cents)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dueNow.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-rose-200/90 text-[13px] font-medium">Departs today, no confirmation on file</p>
                      <p className="text-rose-200/90 text-[13px] font-normal tabular-nums shrink-0">
                        {usdExact(sum(dueNow))}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {dueNow.map((r) => (
                        <div key={r.ref} className={`${INSET} px-3.5 py-2.5 flex items-center justify-between gap-3`}>
                          <div className="min-w-0">
                            <p className="text-white/85 text-[13.5px] font-normal">{r.ref}</p>
                            <p className="text-white/40 text-[11.5px] font-normal">
                              {r.departs ? `departs ${formatDay(r.departs)}` : 'no departure date'}
                              {r.insurance_cents > 0 && ` · includes ${usdExact(r.insurance_cents)} Faye policy`}
                              {r.fare_basis === 'latest_price' && ` · priced ${formatDay(r.priced_on)} (booked ${usdExact(r.booked_cents)})`}
                            </p>
                          </div>
                          <p className="text-white/85 text-[14px] font-normal tabular-nums shrink-0">
                            {usdExact(r.cost_cents)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {thisWeek.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-white/70 text-[13px] font-medium">Book this week — paying off or departing within 7 days</p>
                      <p className="text-white/70 text-[13px] font-normal tabular-nums shrink-0">
                        {usdExact(sum(thisWeek))}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {thisWeek.map((r) => (
                        <div key={r.ref} className={`${INSET} px-3.5 py-2.5 flex items-center justify-between gap-3`}>
                          <div className="min-w-0">
                            <p className="text-white/85 text-[13.5px] font-normal">{r.ref}</p>
                            <p className="text-white/40 text-[11.5px] font-normal">
                              {r.trigger === 'payoff'
                                ? `pays off ${formatDay(r.pays_off)}${r.departs ? ` · departs ${formatDay(r.departs)}` : ''}`
                                : `departs ${formatDay(r.departs)} · book by ${formatDay(r.book_by)}`}
                              {r.insurance_cents > 0 && ` · includes ${usdExact(r.insurance_cents)} Faye policy`}
                              {r.fare_basis === 'latest_price' && ` · priced ${formatDay(r.priced_on)} (booked ${usdExact(r.booked_cents)})`}
                            </p>
                          </div>
                          <p className="text-white/85 text-[14px] font-normal tabular-nums shrink-0">
                            {usdExact(r.cost_cents)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.outlay.insurance_due_now?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-white/70 text-[13px] font-medium">Ticketed, Faye policy still to buy</p>
                      <p className="text-white/70 text-[13px] font-normal tabular-nums shrink-0">
                        {usdExact(data.outlay.insurance_due_now_cents)}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {data.outlay.insurance_due_now.map((r) => (
                        <div key={r.ref} className={`${INSET} px-3.5 py-2.5 flex items-center justify-between gap-3`}>
                          <div className="min-w-0">
                            <p className="text-white/85 text-[13.5px] font-normal">{r.ref}</p>
                            <p className="text-white/40 text-[11.5px] font-normal">
                              {r.departs ? `departs ${formatDay(r.departs)} · ` : ''}premium owed to Faye now
                            </p>
                          </div>
                          <p className="text-white/85 text-[14px] font-normal tabular-nums shrink-0">
                            {usdExact(r.cost_cents)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.outlay.departed_unbooked?.length > 0 && (
                  <div>
                    <div className="mb-2">
                      <p className="text-amber-200/90 text-[13px] font-medium">Departed with no confirmation on file</p>
                      <p className="text-white/40 text-[11.5px] font-normal leading-snug">
                        Not counted as money owed. Record the confirmation number, or fix the trip&rsquo;s status.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {data.outlay.departed_unbooked.map((r) => (
                        <div key={r.ref} className={`${INSET} px-3.5 py-2.5 flex items-center justify-between gap-3`}>
                          <div className="min-w-0">
                            <p className="text-white/85 text-[13.5px] font-normal">{r.ref}</p>
                            <p className="text-white/40 text-[11.5px] font-normal">departed {formatDay(r.departs)}</p>
                          </div>
                          <p className="text-white/55 text-[14px] font-normal tabular-nums shrink-0">
                            {usdExact(r.cost_cents)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}

            {/* What could still move the number */}
            <div className={`${PANEL} px-4 py-4 mb-3`}>
              <h2 className="text-white/85 text-[15px] font-medium mb-3">Watch-outs</h2>
              <div className={`grid grid-cols-1 gap-2.5 ${data.payoffs ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
                <div className={`${INSET} px-3.5 py-3`}>
                  <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">Past due</p>
                  <p className="text-amber-300/90 text-[15px] font-normal tabular-nums">
                    {usdExact(data.inflow.past_due_gross_cents)}
                  </p>
                  <p className="text-white/35 text-[11.5px] font-normal mt-1 leading-snug">
                    charged, not collected — excluded from every balance above
                  </p>
                </div>
                <div className={`${INSET} px-3.5 py-3`}>
                  <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">On failing plans</p>
                  <p className="text-amber-300/90 text-[15px] font-normal tabular-nums">
                    {usdExact(data.inflow.at_risk_cents)}
                  </p>
                  <p className="text-white/35 text-[11.5px] font-normal mt-1 leading-snug">
                    {data.inflow.failing_trips} trip{data.inflow.failing_trips === 1 ? '' : 's'} declining now — counted as inflow anyway
                  </p>
                </div>
                <div className={`${INSET} px-3.5 py-3`}>
                  <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">Never scheduled</p>
                  <p className="text-amber-300/90 text-[15px] font-normal tabular-nums">
                    {usdExact(data.inflow.unscheduled_cents)}
                  </p>
                  <p className="text-white/35 text-[11.5px] font-normal mt-1 leading-snug">
                    owed on plans whose subscription cancels before the balance clears
                  </p>
                </div>
                {data.payoffs && (
                  <div className={`${INSET} px-3.5 py-3`}>
                    <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">Payoffs, last 4 weeks</p>
                    <p className="text-amber-300/90 text-[15px] font-normal tabular-nums">
                      {usd(data.payoffs.ticket_cents_per_week)}/week
                    </p>
                    <p className="text-white/35 text-[11.5px] font-normal mt-1 leading-snug">
                      {data.payoffs.payoffs} trips paid off, {data.payoffs.per_week} a week, each owed a ticket that day.
                      Scheduled payoffs are in the balance; early ones can&rsquo;t be dated
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Provenance */}
            <div className="rounded-2xl border border-brand-400/20 bg-brand-500/[0.05] px-4 py-3.5 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-brand-200/80 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0 space-y-1.5">
                <p className="text-white/85 text-[13.5px] font-normal">
                  Every dollar is dated by the day it reaches the bank.
                </p>
                <p className="text-white/50 text-[13px] font-normal leading-relaxed">
                  Scheduled inflow is each trip&rsquo;s actual outstanding installments — NMI from our
                  own schedule rows, Stripe from the live subscription&rsquo;s locked amount — converted
                  to bank deposits at the Settlement Desk&rsquo;s fitted rates and dated on its funding
                  calendar. Money already charged comes straight from the Settlement Desk, on the day
                  it lands. Airfare is the quoted fare, due the day before each unbooked trip departs
                  or the day it pays off, whichever comes first: a paid-off trip is owed its
                  confirmation number (within a day on weekdays), so its ticket is bought then. Trips
                  still paying pay off on their last scheduled payment. Trips with a confirmation
                  number are exempt because that cash already left. A trip with Faye travel insurance also pays its premium to Faye when the
                  policy is bought at ticketing, so a policy not bought yet adds its premium there.
                </p>
                <p className="text-white/40 text-[12.5px] font-normal leading-relaxed flex items-start gap-1.5">
                  <Lock className="w-3 h-3 shrink-0 mt-[3px]" strokeWidth={1.5} />
                  Snapshot regenerates on command only. Admin-only, service-role storage.
                </p>
                <p className="text-white/40 text-[12.5px] font-normal leading-relaxed flex items-start gap-1.5">
                  <Clock className="w-3 h-3 shrink-0 mt-[3px]" strokeWidth={1.5} />
                  Generated {formatDay(String(snapshot?.generated_at).slice(0, 10))},{' '}
                  {agoLabel(snapshot?.generated_at)}.{' '}
                  {snapshot?.payload?.bank_holidays_through
                    ? 'Deposits skip weekends and Federal Reserve holidays.'
                    : 'Bank holidays are not in this snapshot. Refresh to include them.'}
                </p>
              </div>
            </div>

            {/* The "what if" half. Only appears once a refresh has stored the
                primitives it runs on — an older snapshot predates them. */}
            {snapshot?.payload?.sim_inputs ? (
              <div className="mt-6">
                <CashLadderSimulator
                  authedFetch={authedFetch}
                  openingCashCents={Number(snapshot?.payload?.opening?.cash_on_hand_cents) || 0}
                />
              </div>
            ) : (
              <p className="mt-6 text-white/45 text-[13px] leading-relaxed">
                Press Refresh to enable the simulator — this snapshot was generated before it existed.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
